import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";
import useOnClickOutside from "use-onclickoutside";
import {
  clipboardMenuItems,
  closeContextMenu,
  handleMenuItemClick,
  smartFeatureMenuItems,
  tableMenuGroups,
  tableRowMovementItems,
} from "./contextMenuConfig";

/**
 * Constants for menu actions
 * @constant {Object} MENU_ACTIONS
 * @author Abhishek Dhillon
 */
const MENU_ACTIONS = {
  CUT: "cut",
  COPY: "copy",
};

/**
 * @file ContextMenu.js
 * @author Abhishek Dhillon
 * @description A React component that renders a context menu with customizable menu items.
 * The menu supports both default actions (cut, copy, paste) and table-specific actions.
 * It uses Kendo UI components for the popup and buttons.
 */

/**
 * ContextMenu Component
 * @param {Object} props - Component props
 * @param {Object} props.state - Current state of the context menu
 * @param {Function} props.setState - Function to update the context menu state
 * @param {Object} props.viewProps - Properties passed to the view
 * @returns {JSX.Element} Rendered context menu component
 * @author Abhishek Dhillon
 */
const ContextMenu = ({ state, setState, viewProps }) => {
  // Refs and state management
  const menuRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    handleMouseLeave();
    return () => {
      handleMouseLeave();
    };
  }, [state?.offset]);

  // Close menu when clicking outside
  useOnClickOutside(menuRef, () => {
    if (state.type !== "table-properties") {
      closeContextMenu(state, setState);
    }
  });

  // Check if there's any text selected in the editor
  const hasSelection = state?.view?.state?.selection?.empty === false;

  /**
   * Calculates the optimal position for a submenu based on viewport boundaries
   * @param {DOMRect} parentRect - Bounding rectangle of the parent menu item
   * @param {number} submenuWidth - Width of the submenu
   * @param {number} submenuHeight - Height of the submenu
   * @returns {{left: number, top: number}} The calculated position for the submenu
   * @author Abhishek Dhillon
   */
  const calculateSubmenuPosition = (parentRect, submenuWidth, submenuHeight) => {
    const viewportWidth = window?.innerWidth ?? 0;
    const viewportHeight = window?.innerHeight ?? 0;

    // Get parent dimensions
    const parentTop = parentRect?.top ?? 0;
    const parentHeight = parentRect?.height ?? 0;
    const parentRight = parentRect?.right ?? 0;
    const parentLeft = parentRect?.left ?? 0;
    const parentCenter = parentTop + parentHeight / 2;

    // Calculate initial position - align middle item with parent
    let left = parentRight + 2;
    let top = parentCenter - submenuHeight / 2;

    // Check if submenu would overflow right edge
    if (left + submenuWidth > viewportWidth) {
      // Position submenu to the left of the parent menu
      left = parentLeft - submenuWidth - 2;
    }

    // Check if submenu would overflow top edge
    if (top < 0) {
      // Align with top of parent if submenu would go above viewport
      top = parentTop;
    }

    // Check if submenu would overflow bottom edge
    if (top + submenuHeight > viewportHeight) {
      // Align with bottom of parent if submenu would go below viewport
      top = Math.min(viewportHeight - submenuHeight, parentTop + parentHeight - submenuHeight);
    }

    return { left, top: top + 20 };
  };

  /**
   * Handles mouse enter event for menu items
   * @param {Event} event - Mouse event
   * @param {Object} item - Menu item that was hovered
   * @author Abhishek Dhillon
   */
  const handleMouseEnter = (event, item) => {
    const rect = event?.currentTarget?.getBoundingClientRect();
    const submenuWidth = 200; // Based on min-width in CSS
    const submenuHeight = item?.subItems?.length * 35 ?? 0; // Approximate height per item
    const position = calculateSubmenuPosition(rect, submenuWidth, submenuHeight);
    setSubmenuPosition(position);
    setHoveredItem(item);
  };

  /**
   * Handles mouse leave event for menu items
   */
  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  /**
   * Renders a submenu for a menu item
   * @param {Object} item - Parent menu item
   * @returns {JSX.Element|null} Rendered submenu or null
   */
  const renderSubmenu = (item) => {
    if (!hoveredItem || hoveredItem.text !== item.text) return null;

    return (
      <div
        className={`context-menu-container submenu-position`}
        style={{
          left: submenuPosition.left,
          top: submenuPosition.top,
        }}
        onMouseEnter={() => setHoveredItem(item)}
        onMouseLeave={handleMouseLeave}>
        {item.subItems?.map((subItem, index) => (
          <div
            key={index}
            className='context-menu-item'
            onClick={() => {
              if (subItem.id !== "table-properties") {
                closeContextMenu(state, setState);
              } else {
                setState({ ...state, type: "table-properties" });
              }
            }}>
            <subItem.action view={state?.view}>{subItem?.text}</subItem.action>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders a submenu for row movement items
   * @param {Object} item - Parent menu item
   * @returns {JSX.Element|null} Rendered submenu or null
   */
  const renderRowMovementSubmenu = (item) => {
    if (!hoveredItem || hoveredItem.text !== item.text) return null;

    return (
      <div
        className='context-menu-container submenu-position'
        style={{
          left: submenuPosition.left,
          top: submenuPosition.top,
        }}
        onMouseEnter={() => setHoveredItem(item)}
        onMouseLeave={handleMouseLeave}>
        {item.subItems?.map((subItem, index) => (
          <div key={index} className='context-menu-item'>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                subItem.action(state, setState, viewProps);
              }}>
              {subItem?.customIcon} {subItem?.text}
            </Button>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders a menu item button
   * @param {Object} item - Menu item to render
   * @param {number} index - Index of the menu item
   * @param {boolean} isDisabled - Whether the button should be disabled
   * @returns {JSX.Element} Rendered menu item button
   */
  const renderMenuItem = (item, index, isDisabled = false) => (
    <div key={index} className='context-menu-item'>
      <Button
        className={isDisabled ? "disabled" : ""}
        onClick={(e) => {
          e.stopPropagation();
          if (isDisabled) return;
          handleMenuItemClick(item, state, setState, viewProps);
        }}
        icon={item?.icon}
        disabled={isDisabled}>
        {item?.customIcon} {item?.text}
      </Button>
    </div>
  );

  return (
    <Popup show={state.show} offset={state.offset}>
      <div ref={menuRef} className={`context-menu-container ${state.type === "table-properties" ? "d-none" : ""}`}>
        {/* Clipboard Menu Items */}
        {clipboardMenuItems.map((item, index) =>
          renderMenuItem(item, index, (item.action === MENU_ACTIONS.CUT || item.action === MENU_ACTIONS.COPY) && !hasSelection)
        )}

        {/* Smart Feature Menu Items */}
        <div className='context-menu-devider pt-2 mt-2'>{smartFeatureMenuItems.map((item, index) => renderMenuItem(item, index))}</div>

        {/* Table Menu */}
        {state.isTableMenu && (
          <div className='context-menu-devider pt-2 mt-2'>
            {/* Table Operations Submenu */}
            {tableMenuGroups.map((item, index) => (
              <div key={index} className='context-menu-item' onMouseEnter={(e) => handleMouseEnter(e, item)} onMouseLeave={handleMouseLeave}>
                <Button icon={item?.icon}>{item?.text}</Button>
                {renderSubmenu(item)}
              </div>
            ))}

            {/* Row Movement Items */}
            {tableRowMovementItems.map((item, index) => (
              <div key={index} className='context-menu-item' onMouseEnter={(e) => handleMouseEnter(e, item)} onMouseLeave={handleMouseLeave}>
                <Button icon={item?.icon}>
                  {item?.customIcon} {item?.text}
                </Button>
                {renderRowMovementSubmenu(item)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Popup>
  );
};

export default ContextMenu;
